import React, { useState, useContext, useEffect, useMemo } from 'react';
import { GlobalDataContext, ReactiveJsonSubroot, TemplateContext, evaluateTemplateValue } from "@ea-lab/reactive-json";
import {
  UserSelector,
  ROLES,
  buildTaskTypesMap,
  TaskTypeConfigSection,
  RightsMatrix,
} from '../shared';
import { fetchProjectRights, saveProjectRights } from '../../services/projectRights';
import WorkflowViewModal from '../workflow/WorkflowViewModal';
import { projectsProcessor } from '../../dataProcessors/decoupledApiProcessors';
import { decoupledSave, deleteProject } from '../../services/api';
import TaskTypesPanel from './TaskTypesPanel';
import UserManagementPanel from './UserManagementPanel';
import { useAuth } from '../../context/AuthContext';
import { ConfigurationSidebar } from './ConfigurationSidebar';
import { useConfigurationGroups } from './configurationSections';
import { notify } from '../../notices';

// Onglets de /configuration (cf. `sections` plus bas) — sert à valider ?tab=.
const SECTION_IDS = ['templates', 'taskTypes', 'roles', 'users', 'workflow'];

// Un TEMPLATE de projet EST un groupe `project` portant `is_template` : mêmes
// champs (types de tâches activés, horaires, méthodologie) et MÊME matrice de
// droits qu'un projet, servie par le même endpoint. Aucune structure parallèle
// à maintenir, et la création d'un projet depuis un template se résume à poster
// `templateId` (le back clone en presave/insert).
const TEMPLATES_URL = '/decoupled-api/group/project/collection?display=decoupled&filter[0][f]=is_template&filter[0][v]=1';

const TemplateCard = ({ template, methodologyLabel, isSelected, onSelect, onEdit, onDuplicate, onDelete }) => (
  <div
    onClick={() => onSelect(template.id)}
    className={`relative p-5 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md ${
      isSelected
        ? 'border-iris-500 bg-iris-50 shadow-md'
        : 'border-gray-200 bg-white hover:border-gray-300'
    }`}
  >
    <div className="flex items-start justify-between mb-2">
      <h4 className="font-semibold text-gray-900">{template.name}</h4>
      <div className="flex items-center gap-1">
        <button
          onClick={(e) => { e.stopPropagation(); onEdit(template); }}
          className="p-1.5 text-gray-400 hover:text-iris-600 hover:bg-iris-50 rounded transition-colors"
          title="Modifier"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDuplicate(template); }}
          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
          title="Dupliquer"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(template); }}
          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
          title="Supprimer"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
        </button>
      </div>
    </div>
    <p className="text-sm text-gray-500 mb-3">{template.description || 'Aucune description'}</p>
    <div className="flex items-center gap-3 text-xs">
      {methodologyLabel && (
        <span className="badge badge-info">{methodologyLabel}</span>
      )}
      <span className="text-gray-400">
        {Object.values(template.enabledTaskTypes || {}).filter(Boolean).length} types actifs
      </span>
    </div>
  </div>
);

const TemplateEditor = ({ template, onSave, onCancel, taskTypes, methodologies, saving }) => {
  const isNew = !template?.id;

  const [form, setForm] = useState({
    id: template?.id ?? null,
    name: template?.name || '',
    description: template?.description || '',
    methodology: template?.methodology ? String(template.methodology) : '',
    enabledTaskTypes: template?.enabledTaskTypes || {},
  });
  const [editorTab, setEditorTab] = useState('info');
  const [viewWorkflow, setViewWorkflow] = useState({ isOpen: false, workflowId: null, workflowLabel: null });

  // La matrice de droits n'est PAS un champ du groupe : ce sont ses permissions
  // de groupe, lues et écrites par /api/project/{id}/rights. Un template pas
  // encore enregistré n'a pas d'id, donc pas encore de matrice.
  const [rights, setRights] = useState(null);
  const [rightsError, setRightsError] = useState('');

  useEffect(() => {
    if (isNew) {
      setRights(null);
      setRightsError('');
      return undefined;
    }
    let cancelled = false;
    setRights(null);
    setRightsError('');
    fetchProjectRights(template.id)
      .then((data) => { if (!cancelled) setRights(data); })
      .catch((status) => {
        if (cancelled) return;
        setRightsError(status === 403
          ? "Vous n'avez pas les droits pour gérer la matrice de ce template."
          : "La matrice de droits n'a pas pu être chargée.");
      });
    return () => { cancelled = true; };
  }, [template?.id]);

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  // Seuls les types activés sur le template portent une ligne de droits — même
  // règle que la modale de réglages d'un projet.
  const enabledTypeNames = Object.entries(form.enabledTaskTypes)
    .filter(([, on]) => on)
    .map(([name]) => name);
  const visibleTypes = (rights?.types || []).filter(
    (type) => enabledTypeNames.length === 0 || enabledTypeNames.includes(type.label)
  );

  // La description est obligatoire côté serveur à la création d'un groupe
  // `project` (orbit_group_group_presave) : autant le dire ici.
  const canSave = form.name.trim().length > 0 && form.description.trim().length > 0;

  const tabs = [
    { id: 'info', label: 'Informations' },
    { id: 'types', label: 'Types de tâches' },
    { id: 'rights', label: 'Matrice de droits' },
  ];

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          {isNew ? 'Nouveau template' : `Modifier : ${template.name}`}
        </h3>
        <div className="flex gap-2">
          <button onClick={onCancel} className="btn btn-secondary btn-sm" disabled={saving}>
            Annuler
          </button>
          <button
            onClick={() => onSave(form, rights)}
            className="btn btn-primary btn-sm"
            disabled={!canSave || saving}
            title={canSave ? undefined : 'Un nom et une description sont requis'}
          >
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>

      <div className="flex border-b border-gray-200">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`px-5 py-2.5 text-sm font-medium transition-colors ${
              editorTab === tab.id
                ? 'text-iris-700 border-b-2 border-iris-700 bg-iris-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
            onClick={() => setEditorTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="p-6">
        {editorTab === 'info' && (
          <div className="space-y-4 max-w-lg">
            <div>
              <label className="field-label mb-1" htmlFor="template-name">Nom du template</label>
              <input
                id="template-name"
                type="text"
                value={form.name}
                onChange={(e) => updateField('name', e.target.value)}
                className="input"
                placeholder="Ex: Kanban Standard"
              />
            </div>
            <div>
              <label className="field-label mb-1" htmlFor="template-description">Description</label>
              <textarea
                id="template-description"
                value={form.description}
                onChange={(e) => updateField('description', e.target.value)}
                className="textarea"
                rows={3}
                placeholder="Description du template..."
              />
            </div>
            <div>
              <label className="field-label mb-1" htmlFor="template-methodology">Méthodologie</label>
              <select
                id="template-methodology"
                value={form.methodology}
                onChange={(e) => updateField('methodology', e.target.value)}
                className="select"
              >
                <option value="">— Aucune —</option>
                {methodologies.map(m => (
                  <option key={m.value} value={String(m.value)}>{m.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {editorTab === 'types' && (
          <TaskTypeConfigSection
            taskTypes={taskTypes}
            enabledTypes={form.enabledTaskTypes}
            onChange={(typeName) => updateField('enabledTaskTypes', {
              ...form.enabledTaskTypes,
              [typeName]: !form.enabledTaskTypes[typeName],
            })}
            onWorkflowClick={(typeName, meta) => meta?.workflowId && setViewWorkflow({
              isOpen: true,
              workflowId: meta.workflowId,
              workflowLabel: typeName,
            })}
          />
        )}

        {editorTab === 'rights' && (
          isNew ? (
            <p className="text-sm text-gray-500 bg-gray-50 border border-dashed border-gray-300 rounded-lg text-center py-8">
              Enregistrez le template pour définir sa matrice de droits par défaut.
            </p>
          ) : (
            <RightsMatrix
              rights={rights}
              onChange={setRights}
              visibleTypes={visibleTypes}
              error={rightsError}
              emptyLabel="Activez des types de tâches pour configurer leurs droits."
            />
          )
        )}
      </div>

      <WorkflowViewModal
        isOpen={viewWorkflow.isOpen}
        onClose={() => setViewWorkflow({ isOpen: false, workflowId: null, workflowLabel: null })}
        workflowId={viewWorkflow.workflowId}
        workflowLabel={viewWorkflow.workflowLabel}
      />
    </div>
  );
};

const ConfigurationPage = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);
  const evalProp = (propName) => evaluateTemplateValue({
    valueToEvaluate: props?.[propName],
    globalDataContext,
    templateContext
  });

  // Onglet demandé par ?tab= — retombe sur « Utilisateurs » si le paramètre est
  // absent (le picto engrenage du shell pointe /configuration tout court) ou si
  // l'id n'existe plus (ex. un vieux lien vers l'ancienne rubrique « Sources de
  // données »). Même rang que dans la colonne : « Utilisateurs » y est en tête.
  const requestedSection = new URLSearchParams(window.location.search).get('tab');
  const initialSection = SECTION_IDS.includes(requestedSection) ? requestedSection : 'users';
  const [activeSection, setActiveSection] = useState(initialSection);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  const initialRoles = evalProp('roles') || ROLES;
  const initialDefaultSettings = evalProp('defaultSettings') || {};

  const backendTaskTypes = evalProp('backendTaskTypes');
  const backendMethodologies = evalProp('backendMethodologies');

  const hasBackendTaskTypes = Array.isArray(backendTaskTypes) && backendTaskTypes.length > 0
    && backendTaskTypes.some(t => t?.label || t?.name);
  const hasBackendMethodologies = Array.isArray(backendMethodologies) && backendMethodologies.length > 0
    && backendMethodologies.some(m => m?.label || m?.name);

  // La méthodologie d'un projet est une référence de terme : les options sont
  // les termes du vocabulaire `methodology`, jamais une liste en dur (un libellé
  // ne serait pas un id valide au save).
  const methodologies = useMemo(() => {
    if (!hasBackendMethodologies) return [];
    return backendMethodologies
      .filter(m => m?.label || m?.name)
      .map(m => ({ value: m.value || m.id || m.label, label: m.label || m.name }));
  }, [backendMethodologies, hasBackendMethodologies]);

  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [roles, setRoles] = useState(initialRoles);
  const [newRole, setNewRole] = useState('');

  // Route all Configuration-page feedback through the app-standard notice
  // system (bottom-right toasts used everywhere) instead of an ad-hoc toast.
  const showToast = (message, type = 'success') => notify({ type, message });

  const persistToContext = (key, value) => {
    if (globalDataContext.updateData) {
      globalDataContext.updateData(value, key);
    }
  };

  // Les templates sont des groupes : on les relit après chaque écriture plutôt
  // que de maintenir une copie locale qui divergerait du serveur.
  const loadTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const res = await fetch(TEMPLATES_URL, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const raw = await res.json();
      setTemplates(projectsProcessor({
        requestContext: { url: TEMPLATES_URL },
        responseContext: { status: res.status },
        dataToProcess: raw,
      }) || []);
    } catch {
      showToast("Les templates de projet n'ont pas pu être chargés", 'error');
    } finally {
      setTemplatesLoading(false);
    }
  };

  useEffect(() => { loadTemplates(); }, []);

  const methodologyLabel = (id) => methodologies.find(m => String(m.value) === String(id))?.label || '';

  const handleSaveTemplate = async (form, rights) => {
    const data = {
      name: form.name.trim(),
      description: form.description.trim(),
      methodology: form.methodology ? String(form.methodology) : '',
      isTemplate: true,
    };

    // Les types activés partent en ids de termes (le champ est une référence
    // multi-valeur, le tableau complet remplace la valeur). Tant que la carte
    // des types n'est pas chargée, tout nom résoudrait à rien et viderait la
    // sélection : on ne touche alors pas au champ.
    const { meta: typeMeta } = buildTaskTypesMap(hasBackendTaskTypes ? backendTaskTypes : null);
    if (Object.keys(typeMeta).length > 0) {
      data.enabledTaskTypes = Object.entries(form.enabledTaskTypes)
        .filter(([, on]) => on)
        .map(([name]) => typeMeta[name]?.tid)
        .filter((tid) => tid != null)
        .map(Number);
    }

    setSavingTemplate(true);
    try {
      const saved = await decoupledSave('group', 'project', data, form.id);
      // À la création, l'id revient à la RACINE de la réponse (pas dans `data`).
      const templateId = form.id || saved?.id;
      if (rights && templateId) {
        await saveProjectRights(templateId, rights);
      }
      await loadTemplates();
      setEditingTemplate(null);
      setIsCreating(false);
      showToast(form.id ? 'Template mis à jour' : 'Template créé');
    } catch (e) {
      showToast(e?.message || "Le template n'a pas pu être enregistré", 'error');
    } finally {
      setSavingTemplate(false);
    }
  };

  // La copie emprunte le MÊME chemin que « créer un projet depuis un template » :
  // `templateId` déclenche le clonage serveur (types de tâches, horaires,
  // méthodologie, puis matrice de droits après la réponse).
  const handleDuplicateTemplate = async (template) => {
    setSavingTemplate(true);
    try {
      await decoupledSave('group', 'project', {
        name: `${template.name} (copie)`,
        description: template.description || `Copie de « ${template.name} »`,
        isTemplate: true,
        templateId: String(template.id),
      });
      await loadTemplates();
      showToast('Template dupliqué');
    } catch (e) {
      showToast(e?.message || "Le template n'a pas pu être dupliqué", 'error');
    } finally {
      setSavingTemplate(false);
    }
  };

  // Corbeille (soft-delete) et non suppression sèche : un template a un membre
  // créateur, et supprimer un groupe sans passer par la corbeille laisse des
  // adhésions orphelines qui cassent le calcul de permissions.
  const handleDeleteTemplate = async (template) => {
    if (!window.confirm(`Supprimer le template « ${template.name} » ?`)) return;
    try {
      await deleteProject(template.id);
      if (selectedTemplateId === template.id) setSelectedTemplateId(null);
      await loadTemplates();
      showToast('Template supprimé', 'info');
    } catch (e) {
      showToast(e?.message || "Le template n'a pas pu être supprimé", 'error');
    }
  };

  const handleAddRole = () => {
    if (!newRole.trim() || roles.includes(newRole.trim())) return;
    const updated = [...roles, newRole.trim()];
    setRoles(updated);
    persistToContext('roles', updated);
    setNewRole('');
    showToast('Rôle ajouté');
  };

  const handleRemoveRole = (role) => {
    const updated = roles.filter(r => r !== role);
    setRoles(updated);
    persistToContext('roles', updated);
    showToast('Rôle supprimé', 'info');
  };

  // Receives the enabled top-level term ids resolved by TaskTypesPanel (which
  // owns the task-type fetch), so the saved ids always match the toggle UI.
  const handleSaveDefaultTypes = async (tids) => {
    try {
      const res = await fetch('/api/admin/default-settings', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabledTaskTypes: tids }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Refresh local reactive state so a re-render keeps the saved set.
      persistToContext('defaultSettings.enabledTaskTypes', tids);
      showToast('Types de tâches par défaut enregistrés');
    } catch {
      showToast("Échec de l'enregistrement des types par défaut", 'error');
    }
  };


  const visibleGroups = useConfigurationGroups();

  // Sélectionner un onglet remet aussi à zéro l'édition en cours : rester sur
  // un template ouvert alors qu'on a changé de rubrique n'aurait aucun sens.
  const selectSection = (id) => {
    setActiveSection(id);
    setEditingTemplate(null);
    setIsCreating(false);
    const url = new URL(window.location.href);
    url.searchParams.set('tab', id);
    window.history.replaceState(null, '', url.toString());
  };

  // Si ?tab= (ou la valeur par défaut) désigne un onglet devenu interdit, on
  // retombe sur le premier onglet réellement autorisé plutôt que d'afficher un
  // panneau vide.
  const allowedTabIds = visibleGroups.flatMap(g => g.items.filter(i => i.id).map(i => i.id));
  const effectiveSection = allowedTabIds.includes(activeSection)
    ? activeSection
    : (allowedTabIds[0] ?? null);

  return (
    <div className="flex flex-1 min-h-0 w-full overflow-hidden">
      <ConfigurationSidebar activeSection={effectiveSection} onSelect={selectSection} />

      {/* Main Content — sole vertical scroll region for long sections */}
      <main className="min-h-0 flex-1 overflow-y-auto bg-gray-50 p-8">
        {/* Templates Section */}
        {effectiveSection === 'templates' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Templates de projet</h3>
                <p className="text-sm text-gray-500 mt-1">Gérez les templates utilisés lors de la création de projets</p>
              </div>
              {!editingTemplate && !isCreating && (
                <button
                  onClick={() => { setIsCreating(true); setEditingTemplate({}); }}
                  className="btn btn-primary"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Nouveau template
                </button>
              )}
            </div>

            {editingTemplate || isCreating ? (
              <TemplateEditor
                template={editingTemplate}
                onSave={handleSaveTemplate}
                onCancel={() => { setEditingTemplate(null); setIsCreating(false); }}
                taskTypes={hasBackendTaskTypes ? backendTaskTypes : []}
                methodologies={methodologies}
                saving={savingTemplate}
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {templates.map(template => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    methodologyLabel={methodologyLabel(template.methodology)}
                    isSelected={selectedTemplateId === template.id}
                    onSelect={setSelectedTemplateId}
                    onEdit={setEditingTemplate}
                    onDuplicate={handleDuplicateTemplate}
                    onDelete={handleDeleteTemplate}
                  />
                ))}
                {templatesLoading && (
                  <p className="col-span-full text-center py-16 text-gray-400">Chargement des templates…</p>
                )}
                {!templatesLoading && templates.length === 0 && (
                  <div className="col-span-full text-center py-16 text-gray-400 bg-white rounded-lg border-2 border-dashed border-gray-300">
                    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-4 text-gray-300"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                    <p className="text-lg font-medium">Aucun template</p>
                    <p className="text-sm mt-1">Créez votre premier template de projet</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Task Types Section — composant autonome avec fetch propre */}
        {effectiveSection === 'taskTypes' && (
          <TaskTypesPanel
            defaultEnabledTypeIds={initialDefaultSettings.enabledTaskTypes || []}
            onSaveEnabledTypes={handleSaveDefaultTypes}
            showToast={showToast}
          />
        )}

        {/* Roles Section */}
        {effectiveSection === 'roles' && (
          <div>
            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-900">Gestion des rôles</h3>
              <p className="text-sm text-gray-500 mt-1">Configurez les rôles disponibles dans l'application</p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6 max-w-2xl">
              <div className="flex gap-3 mb-6">
                <input
                  type="text"
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddRole()}
                  placeholder="Nom du nouveau rôle..."
                  className="input flex-grow"
                />
                <button
                  onClick={handleAddRole}
                  disabled={!newRole.trim() || roles.includes(newRole.trim())}
                  className="btn btn-primary"
                >
                  Ajouter
                </button>
              </div>

              <div className="space-y-2">
                {roles.map((role, index) => (
                  <div key={role} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors">
                    <div className="flex items-center gap-3">
                      <span className="avatar">
                        {index + 1}
                      </span>
                      <span className="font-medium text-gray-900">{role}</span>
                    </div>
                    <button
                      onClick={() => handleRemoveRole(role)}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                      title="Supprimer ce rôle"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Users Section */}
        {effectiveSection === 'users' && (
          <UserManagementPanel showToast={showToast} />
        )}

        {/* Workflow Section */}
        {effectiveSection === 'workflow' && (
          <div>
            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-900">Flux de travail</h3>
              <p className="text-sm text-gray-500 mt-1">Gérez les workflows de contenu et leurs transitions</p>
            </div>
            <ReactiveJsonSubroot
              props={{
                rjOptions: {
                  rjBuildUrl: '/components/workflow/WorkflowList.yaml',
                },
              }}
            />
          </div>
        )}
      </main>
    </div>
  );
};

/**
 * Permission guard: the Configuration space needs 'access orbit configuration'
 * — the same permission orbit_front_app declares next to its navigation link,
 * so whoever sees the link can open the page. Any role may hold it; the
 * 'administrator' role holds every permission and is therefore covered.
 */
function ConfigurationPageGuard(props) {
  const { user, isAdmin, hasPermission } = useAuth();

  // Session still loading: render nothing to avoid a flash of "access denied".
  if (!user) return null;

  if (!isAdmin && !hasPermission('access orbit configuration')) {
    return (
      <div className="flex flex-1 items-center justify-center p-8 bg-gray-50">
        <div className="max-w-md text-center bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
          <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center text-amber-500">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          </div>
          <h2 className="text-lg font-bold text-gray-900">Accès réservé</h2>
          <p className="text-sm text-gray-500 mt-1">Vous n’avez pas la permission d’accéder à la configuration.</p>
        </div>
      </div>
    );
  }

  return <ConfigurationPage {...props} />;
}

export default ConfigurationPageGuard;
